import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { arrayOf, bool, func, object, string } from 'prop-types';
import { intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { ensureOwnListing } from '../../util/data';
import { getDefaultTimeZoneOnBrowser, timestampToDate } from '../../util/dates';
import { LISTING_STATE_DRAFT, DATE_TYPE_DATETIME, propTypes } from '../../util/types';
import {
  Button,
  IconClose,
  IconEdit,
  IconSpinner,
  InlineTextButton,
  Modal,
  TimeRange,
} from '../../components';
import { EditListingTimeSlotsPlanForm, EditListingTimeSlotsExceptionForm } from '../../forms';

import css from './EditListingTimeSlotsPanel.css';
import moment from 'moment';

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
export const MAX_SEATS_SHARED = 100;
export const MAX_SEATS_PRIVATE = 1;

// We want to sort exceptions on the client-side, maximum pagination page size is 100,
// so we need to restrict the amount of exceptions to that.
const MAX_EXCEPTIONS_COUNT = 100;

const defaultTimeZone = () =>
  typeof window !== 'undefined' ? getDefaultTimeZoneOnBrowser() : 'Etc/UTC';

const format12HourClock = h => (moment(h, 'hh:mm').format('hh:mm A'));

////////////
// Portal //
////////////

// TODO: change all the modals to use portals at some point.
// Portal is used here to circumvent the problems that rise
// from different levels of z-indexes in DOM tree.
// Note: React Portal didn't exist when we originally created modals.

class Portal extends React.Component {
  constructor(props) {
    super(props);
    this.el = document.createElement('div');
  }

  componentDidMount() {
    // The portal element is inserted in the DOM tree after
    // the Modal's children are mounted, meaning that children
    // will be mounted on a detached DOM node. If a child
    // component requires to be attached to the DOM tree
    // immediately when mounted, for example to measure a
    // DOM node, or uses 'autoFocus' in a descendant, add
    // state to Modal and only render the children when Modal
    // is inserted in the DOM tree.
    this.props.portalRoot.appendChild(this.el);
  }

  componentWillUnmount() {
    this.props.portalRoot.removeChild(this.el);
  }

  render() {
    return ReactDOM.createPortal(this.props.children, this.el);
  }
}

/////////////
// Weekday //
/////////////
const findEntry = (availabilityPlan, dayOfWeek) =>
  availabilityPlan.entries.find(d => d.dayOfWeek === dayOfWeek);

const getEntries = (availabilityPlan, dayOfWeek) =>
  availabilityPlan.entries.filter(d => d.dayOfWeek === dayOfWeek);

const Weekday = props => {
  const { availabilityPlan, dayOfWeek, openEditModal, isPrivateSpace, intl } = props; // eslint-disable-line no-unused-vars
  const hasEntry = findEntry(availabilityPlan, dayOfWeek);

  // const availableForClassBookingsMessage = intl.formatMessage({
  //   id: 'EditListingTimeSlotsPanel.availableForClassBookings'
  // });

  return (
    <div
      className={classNames(css.weekDay, { [css.blockedWeekDay]: !hasEntry })}
      onClick={() => openEditModal(true)}
      role="button"
    >
      <div className={css.dayOfWeek}>
        <FormattedMessage id={`EditListingTimeSlotsPanel.dayOfWeek.${dayOfWeek}`} />
      </div>
      <div className={css.entries}>
        {availabilityPlan && hasEntry
          ? getEntries(availabilityPlan, dayOfWeek).map(e => {
            return (
              // ISSUE: Due to the 24 hour problem this work around that saves the 24h as 23:55 to fix the day and month bug in the calendar
              <span className={css.entry} key={`${e.dayOfWeek}${e.startTime}`}>{`${
                format12HourClock(e.startTime)
                } - ${format12HourClock(e.endTime === "23:55" ? "24:00" : e.endTime)}`}</span>
            );
          })
          : null}
      </div>
    </div>
  );
};

///////////////////////////////////////////////////
// EditListingAvailabilityExceptionPanel - utils //
///////////////////////////////////////////////////

// Create initial entry mapping for form's initial values
const createEntryDayGroups = (entries = {}, timeSlotsAvailableForClassBookings) =>
  entries.reduce((groupedEntries, entry) => {
    const { startTime, endTime: endHour, dayOfWeek } = entry;
    const dayGroup = groupedEntries[dayOfWeek] || [];
    const classBooking = timeSlotsAvailableForClassBookings &&
      timeSlotsAvailableForClassBookings.find(t => t.dayOfWeek === dayOfWeek && t.startTime === startTime && t.endTime === endHour) ?
      true :
      false;
    return {
      ...groupedEntries,
      [dayOfWeek]: [
        ...dayGroup,
        {
          startTime,
          // ISSUE: THIS MIGHT BE THE ISSUE FOR THE 24:00 TIME
          endTime: endHour === '00:00' ? '24:00' : endHour,
          classBooking
        },
      ],
    };
  }, {});

// Create initial values
const createInitialValues = (availabilityPlan, timeSlotsAvailableForClassBookings) => {
  const { timezone, entries } = availabilityPlan || {};
  const tz = timezone || defaultTimeZone();
  return {
    timezone: tz,
    ...createEntryDayGroups(entries, timeSlotsAvailableForClassBookings),
  };
};

// Create entries from submit values
const createEntriesFromSubmitValues = (values, isPrivateSpace) =>
  WEEKDAYS.reduce((allEntries, dayOfWeek) => {
    const dayValues = values[dayOfWeek] || [];
    const dayEntries = dayValues.map(dayValue => {
      const { startTime, endTime } = dayValue;

      // Add classBooking attribute if space is private
      // let extra = {}
      // if (isPrivateSpace) {
      //   extra.classBooking = classBooking || false;
      // }

      let seats = isPrivateSpace ? MAX_SEATS_PRIVATE : MAX_SEATS_SHARED;

      // Note: This template doesn't support seats yet.

      return startTime && endTime
        ? {
          dayOfWeek,
          seats,
          startTime,
          // ISSUE: THIS MIGHT BE THE ISSUE FOR THE 24:00 TIME
          endTime: endTime === '24:00' ? '00:00' : endTime,
          // ...extra
        }
        : null;
    });

    return allEntries.concat(dayEntries.filter(e => !!e));
  }, []);

// Create availabilityPlan from submit values
const createAvailabilityPlan = (values, isPrivateSpace) => ({
  availabilityPlan: {
    type: 'availability-plan/time',
    timezone: values.timezone,
    entries: createEntriesFromSubmitValues(values, isPrivateSpace),
  },
  publicData: {
    timeSlotsAvailableForClassBookings: Object.keys(values).reduce((map, key) => {
      const currentField = values[key];
      const weekDay = WEEKDAYS.find(w => w === key);
      if (weekDay) {
        let timeSlotsWithClassBookings = currentField.filter(c => c.classBooking === true);
        return map.concat(timeSlotsWithClassBookings.map(t => {
          // ISSUE: Due to the 24h issue the saved time as 11:55 will display as 12:00
          const finalEndTime = t.endTime === '11:55' ? '12:00' : t.endTime;
          return { dayOfWeek: key, startTime: t.startTime, endTime: finalEndTime }
        }))
      }
      return map;
    }, []),
    availableTimeSlots: Object.keys(values).reduce((map, k) => {
      if (k === 'timezone') return map;
      return {
        ...map,
        [k]: values[k].reduce((timeslots, timeslot) => {
          // ISSUE: Due to the 24h issue the saved time as 11:55 will display as 12:00
          const finalEndTime = timeslot.endTime === '11:55' ? '12:00' : timeslot.endTime;
          return timeslots.concat({
            startTime: timeslot.startTime,
            endTime: finalEndTime
          })
        }, [])
      }
    }, {}),
  }
});

// Ensure that the AvailabilityExceptions are in sensible order.
//
// Note: if you allow fetching more than 100 exception,
// pagination kicks in and that makes client-side sorting impossible.
const sortExceptionsByStartTime = (a, b) => {
  return a.attributes.start.getTime() - b.attributes.start.getTime();
};

//////////////////////////////////
// EditListingTimeSlotsPanel //
//////////////////////////////////
const EditListingTimeSlotsPanel = props => {
  const {
    className,
    rootClassName,
    listing,
    availabilityExceptions,
    fetchExceptionsInProgress,
    onAddAvailabilityException,
    onDeleteAvailabilityException,
    disabled,
    ready,
    onSubmit,
    onManageDisableScrolling,
    onNextTab,
    submitButtonText,
    updateInProgress,
    errors,
    intl
  } = props;
  // Hooks
  const [isEditPlanModalOpen, setIsEditPlanModalOpen] = useState(false);
  const [isEditExceptionsModalOpen, setIsEditExceptionsModalOpen] = useState(false);
  const [portalRoot, setPortalRoot] = useState(null);
  const [valuesFromLastSubmit, setValuesFromLastSubmit] = useState(null);

  const setPortalRootAfterInitialRender = () => {
    setPortalRoot(document.getElementById('portal-root'));
  };

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureOwnListing(listing);
  // const { publicData } = currentListing.attributes;
  // const isNextButtonDisabled = !publicData.availabilityPlan || publicData.availabilityPlan.entries.length == 0
  const isNextButtonDisabled = !currentListing.attributes.availabilityPlan;
  const isPublished = currentListing.id && currentListing.attributes.state !== LISTING_STATE_DRAFT;
  const defaultAvailabilityPlan = {
    type: 'availability-plan/time',
    timezone: defaultTimeZone(),
    entries: [
      // { dayOfWeek: 'mon', startTime: '09:00', endTime: '17:00', seats: 1 },
      // { dayOfWeek: 'tue', startTime: '09:00', endTime: '17:00', seats: 1 },
      // { dayOfWeek: 'wed', startTime: '09:00', endTime: '17:00', seats: 1 },
      // { dayOfWeek: 'thu', startTime: '09:00', endTime: '17:00', seats: 1 },
      // { dayOfWeek: 'fri', startTime: '09:00', endTime: '17:00', seats: 1 },
      // { dayOfWeek: 'sat', startTime: '09:00', endTime: '17:00', seats: 1 },
      // { dayOfWeek: 'sun', startTime: '09:00', endTime: '17:00', seats: 1 },
    ],
  };
  const { publicData } = currentListing.attributes;
  const availabilityPlan = currentListing.attributes.availabilityPlan || defaultAvailabilityPlan;
  const timeSlotsAvailableForClassBookings = publicData && publicData.timeSlotsAvailableForClassBookings ?
    publicData.timeSlotsAvailableForClassBookings : [];
  const { spaceType } = currentListing.attributes.publicData;
  const isPrivateSpace = spaceType === 'private' ? true : false;

  const initialValues = valuesFromLastSubmit
    ? valuesFromLastSubmit
    : createInitialValues(availabilityPlan, timeSlotsAvailableForClassBookings);

  const handleSubmit = values => {
    setValuesFromLastSubmit(values);

    // Final Form can wait for Promises to return.
    return onSubmit(createAvailabilityPlan(values, isPrivateSpace))
      .then(() => {
        setIsEditPlanModalOpen(false);
      })
      .catch(e => {
        // Don't close modal if there was an error
      });
  };

  const exceptionCount = availabilityExceptions ? availabilityExceptions.length : 0;
  const sortedAvailabilityExceptions = availabilityExceptions.sort(sortExceptionsByStartTime);

  // Save exception click handler
  const saveException = values => {
    const { exceptionStartTime, exceptionEndTime } = values;

    const availability = 'not-available'; // by default availability is going to be not-available

    // TODO: add proper seat handling
    const seats = availability === 'available' ? 1 : 0;

    return onAddAvailabilityException({
      listingId: listing.id,
      seats,
      start: timestampToDate(exceptionStartTime),
      end: timestampToDate(exceptionEndTime),
    })
      .then(() => {
        setIsEditExceptionsModalOpen(false);
      })
      .catch(e => {
        // Don't close modal if there was an error
      });
  };

  return (
    <main className={classes} ref={setPortalRootAfterInitialRender}>
      <h1 className={css.title}>
        {isPublished ? (
          <FormattedMessage
            id="EditListingTimeSlotsPanel.title"
          />
        ) : (
            <FormattedMessage id="EditListingTimeSlotsPanel.createListingTitle" />
          )}
      </h1>

      <section className={css.section}>
        <header className={css.sectionHeader}>
          <h2 className={css.sectionTitle}>
            {/* <FormattedMessage id="EditListingTimeSlotsPanel.defaultScheduleTitle" /> */}
          </h2>
          <InlineTextButton
            className={css.editPlanButton}
            onClick={() => setIsEditPlanModalOpen(true)}
          >
            <IconEdit className={css.editPlanIcon} />{' '}
            <FormattedMessage id="EditListingTimeSlotsPanel.edit" />
          </InlineTextButton>
        </header>
        <div className={css.week}>
          {WEEKDAYS.map(w => (
            <Weekday
              dayOfWeek={w}
              key={w}
              availabilityPlan={availabilityPlan}
              isPrivateSpace={isPrivateSpace}
              openEditModal={setIsEditPlanModalOpen}
              intl={intl}
            />
          ))}
        </div>
      </section>
      <section className={css.section}>
        <header className={css.sectionHeader}>
          <h2 className={css.sectionTitle}>
            {fetchExceptionsInProgress ? (
              <FormattedMessage id="EditListingTimeSlotsPanel.availabilityExceptionsTitleNoCount" />
            ) : (
                <FormattedMessage
                  id="EditListingTimeSlotsPanel.availabilityExceptionsTitle"
                  values={{ count: exceptionCount }}
                />
              )}
          </h2>
        </header>
        <div className={css.sectionDescription}>
          <p className={css.sectionParagraph}>
            Exceptions are holidays or specific date(s) during the year that you don't want your
            space to be available for bookings.
          </p>
          <p className={css.sectionParagraph}>
            Adding exceptions to your listing will block out those date(s) from your space's calendar
            and won't allow users to book them.
          </p>
        </div>
        {fetchExceptionsInProgress ? (
          <div className={css.exceptionsLoading}>
            <IconSpinner />
          </div>
        ) : exceptionCount === 0 ? (
          <div className={css.noExceptions}>
            <FormattedMessage id="EditListingTimeSlotsPanel.noExceptions" />
          </div>
        ) : (
              <div className={css.exceptions}>
                {sortedAvailabilityExceptions.map(availabilityException => {
                  const { start, end, seats } = availabilityException.attributes;
                  return (
                    <div key={availabilityException.id.uuid} className={css.exception}>
                      <div className={css.exceptionHeader}>
                        <div className={css.exceptionAvailability}>
                          <div
                            className={classNames(css.exceptionAvailabilityDot, {
                              [css.isAvailable]: seats > 0,
                            })}
                          />
                          <div className={css.exceptionAvailabilityStatus}>
                            {seats > 0 ? (
                              <FormattedMessage id="EditListingTimeSlotsPanel.exceptionAvailable" />
                            ) : (
                                <FormattedMessage id="EditListingTimeSlotsPanel.exceptionNotAvailable" />
                              )}
                          </div>
                        </div>
                        <button
                          className={css.removeExceptionButton}
                          onClick={() =>
                            onDeleteAvailabilityException({ id: availabilityException.id })
                          }
                        >
                          <IconClose size="normal" className={css.removeIcon} />
                        </button>
                      </div>
                      <TimeRange
                        className={css.timeRange}
                        startDate={start}
                        endDate={end}
                        dateType={DATE_TYPE_DATETIME}
                        timeZone={availabilityPlan.timezone}
                      />
                    </div>
                  );
                })}
              </div>
            )}
        {exceptionCount <= MAX_EXCEPTIONS_COUNT ? (
          <InlineTextButton
            className={css.addExceptionButton}
            onClick={() => setIsEditExceptionsModalOpen(true)}
            disabled={disabled}
            ready={ready}
          >
            <FormattedMessage id="EditListingTimeSlotsPanel.addException" />
          </InlineTextButton>
        ) : null}
      </section>

      {errors.showListingsError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingTimeSlotsPanel.showListingFailed" />
        </p>
      ) : null}

      {errors.publishListingError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingTimeSlotsPanel.publishListingFailed" />
        </p>
      ) : null}

      {!isPublished ? (
        <Button
          className={css.goToNextTabButton}
          onClick={onNextTab}
          disabled={isNextButtonDisabled}
        >
          {submitButtonText}
        </Button>
      ) : null}
      {portalRoot && onManageDisableScrolling ? (
        <Portal portalRoot={portalRoot}>
          <Modal
            id="EditTimeSlotsPlan"
            isOpen={isEditPlanModalOpen}
            onClose={() => setIsEditPlanModalOpen(false)}
            onManageDisableScrolling={onManageDisableScrolling}
            containerClassName={css.modalContainer}
          >
            <EditListingTimeSlotsPlanForm
              formId="EditListingTimeSlotsPlanForm"
              listingTitle={currentListing.attributes.title}
              availabilityPlan={availabilityPlan}
              weekdays={WEEKDAYS}
              onSubmit={handleSubmit}
              initialValues={initialValues}
              inProgress={updateInProgress}
              fetchErrors={errors}
              isPrivateSpace={isPrivateSpace}
            />
          </Modal>
        </Portal>
      ) : null}
      {portalRoot && onManageDisableScrolling ? (
        <Portal portalRoot={portalRoot}>
          <Modal
            id="EditAvailabilityExceptions"
            isOpen={isEditExceptionsModalOpen}
            onClose={() => setIsEditExceptionsModalOpen(false)}
            onManageDisableScrolling={onManageDisableScrolling}
            containerClassName={css.modalContainer}
          >
            <EditListingTimeSlotsExceptionForm
              formId="EditListingTimeSlotsExceptionForm"
              onSubmit={saveException}
              timeZone={availabilityPlan.timezone}
              availabilityExceptions={sortedAvailabilityExceptions}
              updateInProgress={updateInProgress}
              fetchErrors={errors}
            />
          </Modal>
        </Portal>
      ) : null}
    </main>
  );
};

EditListingTimeSlotsPanel.defaultProps = {
  className: null,
  rootClassName: null,
  listing: null,
  availabilityExceptions: [],
};

EditListingTimeSlotsPanel.propTypes = {
  className: string,
  rootClassName: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  availabilityExceptions: arrayOf(propTypes.availabilityException),
  fetchExceptionsInProgress: bool.isRequired,
  onAddAvailabilityException: func.isRequired,
  onDeleteAvailabilityException: func.isRequired,
  onSubmit: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onNextTab: func.isRequired,
  submitButtonText: string.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
  intl: intlShape.isRequired,
};

export default EditListingTimeSlotsPanel;
